import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { identityRolesApi } from '@/api/services/identityRoles';
import type {
  IdentityRoleCreateDto,
  IdentityRoleUpdateDto,
  UpdatePermissionsDto,
} from '@/api/types/identity';

export const identityRoleKeys = {
  all: ['identityRoles'] as const,
  list: ['identityRoles', 'list'] as const,
  role: (id: string) => ['identityRoles', 'role', id] as const,
  permissions: (roleName: string) => ['identityRoles', 'permissions', roleName] as const,
};

export function useRole(id: string | null) {
  return useQuery({
    queryKey: identityRoleKeys.role(id ?? ''),
    queryFn: () => identityRolesApi.get(id as string),
    enabled: id !== null,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IdentityRoleCreateDto) => identityRolesApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityRoleKeys.list }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: IdentityRoleUpdateDto }) =>
      identityRolesApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityRoleKeys.list }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => identityRolesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: identityRoleKeys.list }),
  });
}

export function useRolePermissions(roleName: string | null) {
  return useQuery({
    queryKey: identityRoleKeys.permissions(roleName ?? ''),
    queryFn: () => identityRolesApi.getPermissions(roleName as string),
    enabled: roleName !== null,
  });
}

export function useUpdateRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleName, input }: { roleName: string; input: UpdatePermissionsDto }) =>
      identityRolesApi.updatePermissions(roleName, input),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: identityRoleKeys.permissions(variables.roleName) }),
  });
}
